import { FunctionComponent, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import { MIGRATION_ENTITIES_BY_AGENCY, MIGRATION_AGENCY_BY_ID, EMPLOYEES_BY_AGENCY_ID } from "../../api/query";
import { BULK_ASSIGN_MEMBERS_TO_ENTITIES, BULK_REMOVE_MEMBERS_FROM_ENTITIES } from "../../api/mutation";
import { useAuth } from "../../context/AuthContext";

const ManageAssignments: FunctionComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const agencyData = location.state?.agency;
    const { userInfo } = useAuth();
    const organizationID = userInfo?.agencies?.id || 0;

    const [activeRecordType, setActiveRecordType] = useState<"client" | "caregiver" | "employee" | "prospect">("client");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEntityIds, setSelectedEntityIds] = useState<number[]>([]);
    const [expandedMemberId, setExpandedMemberId] = useState<number | null>(null);
    // entity ids selected for removal, keyed by member id
    const [selectedToRemove, setSelectedToRemove] = useState<Record<number, number[]>>({});

    // ── Queries ──────────────────────────────────────────────────────────────
    const { data: agencyDetail, loading: agencyLoading } = useQuery(MIGRATION_AGENCY_BY_ID, {
        variables: { agency_id: parseInt(agencyData?.id) },
        fetchPolicy: "no-cache",
        skip: !agencyData?.id,
    });

    const { data: employeesData, loading: employeesLoading } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
        variables: { agency_id: parseInt(organizationID) },
        fetchPolicy: "no-cache",
        skip: !organizationID,
    });

    const { data: entitiesData, loading: entitiesLoading, refetch: refetchEntities } = useQuery(MIGRATION_ENTITIES_BY_AGENCY, {
        variables: { agency_id: parseInt(agencyData?.id) },
        fetchPolicy: "no-cache",
        skip: !agencyData?.id,
    });

    // ── Mutations ─────────────────────────────────────────────────────────────
    const [bulkAssign, { loading: assigning }] = useMutation(BULK_ASSIGN_MEMBERS_TO_ENTITIES, { fetchPolicy: "no-cache" });
    const [bulkRemove, { loading: removing }] = useMutation(BULK_REMOVE_MEMBERS_FROM_ENTITIES, { fetchPolicy: "no-cache" });

    // ── Derived data ──────────────────────────────────────────────────────────
    const assignedMembers: any[] = agencyDetail?.migration_agency_by_id?.agency?.assigned_team_members || [];
    const allEmployees: any[] = employeesData?.employees_by_agency_id?.employees || [];
    const allEntities: any[] = entitiesData?.migration_entities_by_agency?.entities || [];

    // Only show team members that are assigned to this agency
    const teamMembers = useMemo(() => assignedMembers.map((m: any) => {
        const emp = allEmployees.find((e: any) => Number(e.employee_id) === Number(m.member_id));
        const name = emp ? `${emp.first_name} ${emp.last_name}` : `Member ${m.member_id}`;
        const initials = emp
            ? `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`.toUpperCase()
            : String(m.member_id).slice(-2);
        return { id: Number(m.member_id), name, initials };
    }), [assignedMembers, allEmployees]);

    const currentEntities = useMemo(() =>
        allEntities.filter(e => e.entity_type === activeRecordType),
        [allEntities, activeRecordType]
    );

    const filteredEntities = useMemo(() =>
        currentEntities.filter(e =>
            `${e.first_name} ${e.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [currentEntities, searchTerm]
    );

    const unassignedEntities = filteredEntities.filter(e => !e.member_id);
    const assignedEntities = filteredEntities.filter(e => !!e.member_id);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const toggleSelect = (id: number) =>
        setSelectedEntityIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const selectAllUnassigned = () => setSelectedEntityIds(unassignedEntities.map(e => Number(e.id)));
    const clearSelection = () => setSelectedEntityIds([]);

    const handleSplitEqually = async () => {
        if (!teamMembers.length || !unassignedEntities.length) return;
        // Chunked distribution — consecutive blocks per member
        const total = unassignedEntities.length;
        const count = teamMembers.length;
        const chunkSize = Math.ceil(total / count);
        const assignments = unassignedEntities.map((entity, idx) => ({
            entity_id: Number(entity.id),
            member_id: teamMembers[Math.min(Math.floor(idx / chunkSize), count - 1)].id,
        }));
        try {
            const res = await bulkAssign({ variables: { assignments } });
            const result = res.data?.bulk_assign_members_to_entities;
            if (result?.status_code === 200 || result?.status_code === 201) {
                showSuccess(`Split ${result.assigned_count} record(s) equally across ${teamMembers.length} members`);
                refetchEntities();
            } else {
                showError(result?.message || "Split failed");
            }
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        }
    };

    const handleAssign = async (memberId: number) => {
        if (!selectedEntityIds.length) return;
        try {
            const assignments = selectedEntityIds.map(entity_id => ({ entity_id, member_id: memberId }));
            const res = await bulkAssign({ variables: { assignments } });
            const result = res.data?.bulk_assign_members_to_entities;
            if (result?.status_code === 200 || result?.status_code === 201) {
                showSuccess(`Assigned ${result.assigned_count} record(s) successfully`);
                setSelectedEntityIds([]);
                refetchEntities();
            } else {
                showError(result?.message || "Assignment failed");
            }
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        }
    };

    const handleRemove = async (entityIds: number[]) => {
        if (!entityIds.length) return;
        try {
            const res = await bulkRemove({ variables: { entity_ids: entityIds } });
            const result = res.data?.bulk_unassign_members_from_entities;
            if (result?.status_code === 200 || result?.status_code === 201) {
                showSuccess(`Removed ${result.unassigned_count ?? entityIds.length} record(s)`);
                setSelectedToRemove({});
                refetchEntities();
            } else {
                showError(result?.message || "Remove failed");
            }
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        }
    };

    const toggleRemoveSelect = (memberId: number, entityId: number) => {
        setSelectedToRemove(prev => {
            const current = prev[memberId] || [];
            return {
                ...prev,
                [memberId]: current.includes(entityId)
                    ? current.filter(id => id !== entityId)
                    : [...current, entityId],
            };
        });
    };

    const selectAllForRemoval = (memberId: number, entityIds: number[]) => {
        setSelectedToRemove(prev => ({ ...prev, [memberId]: entityIds }));
    };

    const clearRemoveSelection = (memberId: number) => {
        setSelectedToRemove(prev => ({ ...prev, [memberId]: [] }));
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "completed": return "bg-green-100 text-green-700 border-green-300";
            case "in_progress": return "bg-blue-100 text-blue-700 border-blue-300";
            case "issues_found": return "bg-orange-100 text-orange-700 border-orange-300";
            default: return "bg-gray-100 text-gray-700 border-gray-300";
        }
    };

    const loading = agencyLoading || employeesLoading || entitiesLoading;

    const tabLabel = (type: "client" | "caregiver" | "employee" | "prospect") =>
        type === "client" ? "Clients" : type === "caregiver" ? "Caregivers" : type === "employee" ? "Employees" : "Prospects";

    const entityName = (e: any) =>
        [e.last_name, [e.first_name, e.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ");

    return (
        <div className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-[360px] max-w-[100%] leading-[normal] tracking-[normal] text-left text-xl text-colors-text-black font-body-xs-semi-bold">
            <div className="self-stretch bg-gray overflow-hidden flex flex-col items-center justify-start relative gap-[24px] min-h-[913px] max-w-full">
                <div className="w-full flex flex-col items-start justify-start py-40 px-20 mq1490:px-[30px] mq575:px-5 box-border gap-[24px] max-w-[100%]">

                    {/* Header */}
                    <div className="self-stretch flex flex-row items-center justify-between gap-[20px] max-w-full mq825:flex-col mq825:items-start">
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-[5px]">
                                <img
                                    className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
                                    alt="" src={backArrow}
                                    onClick={() => navigate(-1)}
                                />
                                <h3 className="m-0 relative text-inherit font-bold font-inherit dark:text-white">
                                    Manage Assignments
                                </h3>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 m-0 ml-8">
                                {agencyData?.agency_name || "Agency"}
                            </p>
                        </div>
                    </div>

                    {loading ? <Loader /> : (
                    <>
                    {/* Record Type Tabs */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                        <div className="flex border-b border-gray-200 dark:border-gray-700">
                            {(["client", "caregiver", "employee", "prospect"] as const).map(type => {
                                const count = allEntities.filter(e => e.entity_type === type).length;
                                return (
                                    <button
                                        key={type}
                                        className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                            activeRecordType === type
                                                ? "bg-blue-600 text-white"
                                                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        }`}
                                        onClick={() => { setActiveRecordType(type); setSelectedEntityIds([]); setSearchTerm(""); setExpandedMemberId(null); }}
                                    >
                                        {tabLabel(type)} ({count})
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Two-column layout */}
                    <div className="w-full grid grid-cols-2 mq1275:grid-cols-1 gap-6">

                        {/* Left — Team Members */}
                        <div className="flex flex-col gap-4">
                            <h4 className="text-base font-bold text-gray-900 dark:text-white">
                                Team Members
                            </h4>
                            {teamMembers.length === 0 && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">No team members assigned to this agency.</p>
                            )}
                            {teamMembers.map(member => {
                                const memberEntities = currentEntities.filter(e => Number(e.member_id) === member.id);
                                const isExpanded = expandedMemberId === member.id;
                                return (
                                    <div key={member.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                                        {/* Accordion Header */}
                                        <div
                                            className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                            onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center shrink-0">
                                                    {member.initials}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900 dark:text-white m-0">{member.name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
                                                        {memberEntities.length} {tabLabel(activeRecordType).toLowerCase()} assigned
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {selectedEntityIds.length > 0 && (
                                                    <button
                                                        disabled={assigning}
                                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold disabled:opacity-60"
                                                        onClick={e => { e.stopPropagation(); handleAssign(member.id); }}
                                                    >
                                                        {assigning ? "..." : `Assign (${selectedEntityIds.length})`}
                                                    </button>
                                                )}
                                                <svg
                                                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Accordion Body */}
                                        {isExpanded && (
                                            <div className="border-t border-gray-100 dark:border-gray-700 px-5 pb-5 pt-3">
                                                {memberEntities.length > 0 ? (
                                                    <>
                                                        {/* Bulk action bar */}
                                                        <div className="flex items-center justify-between mb-3">
                                                            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-600 dark:text-gray-400">
                                                                <input
                                                                    type="checkbox"
                                                                    className="w-4 h-4 cursor-pointer"
                                                                    checked={(selectedToRemove[member.id]?.length ?? 0) === memberEntities.length}
                                                                    onChange={e => e.target.checked
                                                                        ? selectAllForRemoval(member.id, memberEntities.map(e => Number(e.id)))
                                                                        : clearRemoveSelection(member.id)
                                                                    }
                                                                />
                                                                Select all
                                                            </label>
                                                            <div className="flex gap-2">
                                                                {(selectedToRemove[member.id]?.length ?? 0) > 0 && (
                                                                    <button
                                                                        disabled={removing}
                                                                        className="px-3 py-1 text-xs font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-60"
                                                                        onClick={() => handleRemove(selectedToRemove[member.id])}
                                                                    >
                                                                        {removing ? "Removing..." : `Remove (${selectedToRemove[member.id].length})`}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    disabled={removing}
                                                                    className="px-3 py-1 text-xs font-semibold bg-red-100 hover:bg-red-200 text-red-700 rounded-lg disabled:opacity-60"
                                                                    onClick={() => handleRemove(memberEntities.map(e => Number(e.id)))}
                                                                >
                                                                    Remove All
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="max-h-[300px] overflow-y-auto space-y-2">
                                                            {memberEntities.map(entity => {
                                                                const entityId = Number(entity.id);
                                                                const isChecked = (selectedToRemove[member.id] || []).includes(entityId);
                                                                return (
                                                                    <div
                                                                        key={entity.id}
                                                                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                                                            isChecked
                                                                                ? "bg-red-50 border border-red-300 dark:bg-red-900/20"
                                                                                : "bg-gray-50 dark:bg-gray-700"
                                                                        }`}
                                                                        onClick={() => toggleRemoveSelect(member.id, entityId)}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            className="w-4 h-4 cursor-pointer accent-red-500 shrink-0"
                                                                            checked={isChecked}
                                                                            onChange={() => toggleRemoveSelect(member.id, entityId)}
                                                                            onClick={e => e.stopPropagation()}
                                                                        />
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-gray-900 dark:text-white m-0">{entityName(entity)}</p>
                                                                            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(entity.progress_status)}`}>
                                                                                {entity.progress_status?.replace(/_/g, " ")}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-3">
                                                        No {tabLabel(activeRecordType).toLowerCase()} assigned yet
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Right — Unassigned entities */}
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-base font-bold text-gray-900 dark:text-white">
                                    Unassigned {tabLabel(activeRecordType)} ({unassignedEntities.length})
                                </h4>
                                <div className="flex gap-2">
                                    {selectedEntityIds.length > 0 && (
                                        <button className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800" onClick={clearSelection}>
                                            Clear ({selectedEntityIds.length})
                                        </button>
                                    )}
                                    <button className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 font-semibold" onClick={selectAllUnassigned}>
                                        Select All
                                    </button>
                                    {unassignedEntities.length > 0 && teamMembers.length > 0 && currentEntities.length === unassignedEntities.length && (
                                        <button
                                            disabled={assigning}
                                            className="px-3 py-1 text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-60 flex items-center gap-1"
                                            onClick={handleSplitEqually}
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                            </svg>
                                            {assigning ? "Splitting..." : "Split Equally"}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder={`Search ${tabLabel(activeRecordType).toLowerCase()}...`}
                                    className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 max-h-[700px] overflow-y-auto">
                                {unassignedEntities.length > 0 ? (
                                    <div className="space-y-2">
                                        {unassignedEntities.map(entity => {
                                            const id = Number(entity.id);
                                            const selected = selectedEntityIds.includes(id);
                                            return (
                                                <div
                                                    key={entity.id}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                        selected
                                                            ? "bg-blue-50 border-blue-500 dark:bg-blue-900/30"
                                                            : "bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:border-gray-300"
                                                    }`}
                                                    onClick={() => toggleSelect(id)}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 cursor-pointer"
                                                        checked={selected}
                                                        onChange={() => toggleSelect(id)}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white m-0">{entityName(entity)}</p>
                                                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(entity.progress_status)}`}>
                                                            {entity.progress_status?.replace(/_/g, " ")}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                                        {searchTerm ? "No matching records found" : `All ${tabLabel(activeRecordType).toLowerCase()} have been assigned`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageAssignments;
